// Our own first-party signal, written from the consent banner's callbacks
// (see components/cookie-consent-banner.tsx) — deliberately NOT parsed from
// vanilla-cookieconsent's own `cc_cookie` (an internal JSON blob whose shape
// isn't a public contract), so a library upgrade can't silently break
// analytics gating.
export const ANALYTICS_CONSENT_COOKIE = "analytics_consent";

const GRANTED = "granted";
const DENIED = "denied";

export function hasAnalyticsConsent(cookieHeader: string): boolean {
  return readCookie(cookieHeader, ANALYTICS_CONSENT_COOKIE) === GRANTED;
}

export function setAnalyticsConsentCookie(granted: boolean): void {
  const value = granted ? GRANTED : DENIED;
  // 182 days (15724800s) — deliberately matches vanilla-cookieconsent's own
  // default `expiresAfterDays: 182` for its `cc_cookie` (see
  // components/cookie-consent-banner.tsx's `run()` config, which relies on
  // that library default). If the library's banner cookie expires before
  // ours does, the banner would reappear (no visible consent record) while
  // this cookie still reads "granted" and silently starts PostHog. Keep
  // these two lifetimes in lockstep if either changes.
  const maxAgeSeconds = 182 * 24 * 60 * 60;
  // The library's own cc_cookie sets `secure: true` by default (confirmed
  // in vanilla-cookieconsent's default config) when served over https; do
  // the same here rather than leaving this cookie sendable over plain http.
  // (typeof-guarded because this file's tests run under vitest's "node"
  // environment, which has no `window` global — only `document` is mocked.)
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${ANALYTICS_CONSENT_COOKIE}=${value}; path=/; max-age=${maxAgeSeconds}; samesite=lax${secure}`;
}

function readCookie(cookieHeader: string, name: string): string | undefined {
  return cookieHeader
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

// Extracted from components/cookie-consent-banner.tsx (a component file, so
// not unit-testable there) so the full grant/deny round trip — including
// the sticky-opt-out fix in initAnalyticsIfConsented() — has test coverage.
// initAnalyticsIfConsented/disableAnalytics are passed in as parameters
// rather than imported from "./analytics" directly: analytics.ts already
// imports hasAnalyticsConsent from this module, so importing back would
// create a circular module dependency.
export function syncAnalyticsConsent(
  categories: string[],
  initAnalyticsIfConsented: () => void,
  disableAnalytics: () => void,
): void {
  const granted = categories.includes("analytics");
  setAnalyticsConsentCookie(granted);
  if (granted) {
    initAnalyticsIfConsented();
  } else {
    disableAnalytics();
  }
}
